import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, IconButton, Tooltip, CircularProgress, Button } from "@material-ui/core";
import {
	Close as CloseIcon,
	ChevronLeft,
	ChevronRight,
	GetApp,
	BrokenImage as BrokenImageIcon,
	ChatBubbleOutline,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";

import api from "../../services/api";
import { isRequestCanceled } from "../../utils/requestCancel";
import { normalizeMediaUrl, extractMediaFilename } from "../../utils/mediaUrl";
import downloadMediaFile from "../../utils/downloadMediaFile";
import { i18n } from "../../translate/i18n";

// Janela pedida ao servidor de cada lado, e a distância da ponta em que a
// próxima janela já é buscada (a navegação não espera a rede).
const PAGE_SIZE = 30;
const PREFETCH_DISTANCE = 5;
const SWIPE_MIN_PX = 50;
const DRAG_CLICK_TOLERANCE_PX = 5;

const useStyles = makeStyles(() => ({
	// O visualizador é sempre escuro, como o do WhatsApp: a foto é o conteúdo.
	paper: {
		backgroundColor: "rgba(0, 0, 0, 0.94)",
		color: "#fff",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	header: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		padding: "8px 12px",
		minHeight: 56,
		flexShrink: 0,
	},
	headerInfo: {
		flex: 1,
		minWidth: 0,
		fontSize: 14,
		opacity: 0.85,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	headerButton: {
		color: "#fff",
	},
	goToButton: {
		color: "#fff",
		textTransform: "none",
		marginRight: 4,
	},
	stage: {
		position: "relative",
		flex: 1,
		minHeight: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	viewport: {
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0 72px 24px",
		boxSizing: "border-box",
		overflow: "hidden",
		[`@media (max-width: 600px)`]: {
			padding: "0 8px 16px",
		},
	},
	viewportZoomed: {
		display: "block",
		overflow: "auto",
		padding: 0,
		cursor: "grab",
	},
	image: {
		display: "block",
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "contain",
		cursor: "zoom-in",
		userSelect: "none",
	},
	imageZoomed: {
		maxWidth: "none",
		maxHeight: "none",
		cursor: "zoom-out",
		margin: "auto",
	},
	video: {
		display: "block",
		maxWidth: "100%",
		maxHeight: "100%",
		outline: "none",
		backgroundColor: "#000",
	},
	// Tooltip não aceita filho desabilitado: o slot recebe o hover e posiciona.
	navSlot: {
		position: "absolute",
		top: "50%",
		transform: "translateY(-50%)",
		zIndex: 1,
	},
	navButton: {
		color: "#fff",
		backgroundColor: "rgba(255, 255, 255, 0.12)",
		"&:hover": {
			backgroundColor: "rgba(255, 255, 255, 0.24)",
		},
		"&.Mui-disabled": {
			color: "rgba(255, 255, 255, 0.25)",
			backgroundColor: "transparent",
		},
	},
	navPrev: {
		left: 12,
	},
	navNext: {
		right: 12,
	},
	unavailable: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 8,
		opacity: 0.7,
	},
	unavailableIcon: {
		fontSize: 64,
	},
}));

const isVideoMediaType = (mediaType) => /video/i.test(String(mediaType || ""));

/**
 * Converte uma mensagem (da lista ou da galeria do servidor) no item do
 * visualizador. `overrides` permite à lista passar a URL que ela já resolveu
 * (ex.: depois de um re-download).
 */
export const toMediaGalleryItem = (message, overrides = {}) => {
	const url = normalizeMediaUrl(overrides.url || message?.mediaUrl);
	return {
		id: String(message?.id || ""),
		ticketId: message?.ticketId || null,
		ticketUuid: message?.ticketUuid || message?.ticket?.uuid || null,
		type: isVideoMediaType(overrides.mediaType || message?.mediaType) ? "video" : "image",
		url,
		thumbUrl: normalizeMediaUrl(overrides.thumbUrl || message?.mediaThumbUrl) || undefined,
		fileName: message?.mediaName || extractMediaFilename(url) || "arquivo",
		createdAt: message?.createdAt || null,
		message,
	};
};

const buildSourceRequest = (source) => {
	if (source?.kind === "contact") {
		const filter = source.filter || {};
		return {
			path: `/messages/contact/${source.contactId}/media-gallery`,
			params: {
				...(filter.ticketId ? { ticketId: filter.ticketId } : {}),
				...(filter.whatsappId ? { whatsappId: filter.whatsappId } : {}),
				...(filter.viewAll ? { viewAll: "true" } : {}),
			},
		};
	}
	return { path: `/messages/${source?.ticketId}/media-gallery`, params: {} };
};

/**
 * Fotos e vídeos do histórico visível ao usuário (o backend aplica o mesmo escopo
 * da lista de mensagens). `source`: { kind: "ticket", ticketId } ou
 * { kind: "contact", contactId, filter } — o filtro do seletor da aba Contatos.
 */
export const fetchMediaGallery = async (source, { cursorId, direction, limit = PAGE_SIZE }, signal) => {
	const { path, params } = buildSourceRequest(source);
	const { data } = await api.get(path, {
		params: { ...params, ...(cursorId ? { cursorId } : {}), direction, limit },
		signal,
	});
	return {
		items: Array.isArray(data?.items) ? data.items.map((message) => toMediaGalleryItem(message)) : [],
		hasOlder: Boolean(data?.hasOlder),
		hasNewer: Boolean(data?.hasNewer),
	};
};

const mergeUnique = (head, tail) => {
	const seen = new Set();
	return [...head, ...tail].filter((item) => {
		if (!item?.id || seen.has(item.id)) return false;
		seen.add(item.id);
		return true;
	});
};

const formatItemDate = (value) => {
	if (!value) return "";
	try {
		const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
		return Number.isNaN(parsed.getTime()) ? "" : format(parsed, "dd/MM/yyyy HH:mm");
	} catch (_err) {
		return "";
	}
};

const MediaGalleryViewer = ({
	open,
	onClose,
	initialItem,
	source,
	localItems,
	onGoToMessage,
}) => {
	const classes = useStyles();
	// items e index andam juntos: uma janela anterior que chega desloca o índice.
	const [gallery, setGallery] = useState({ items: [], index: 0 });
	const [edges, setEdges] = useState({ hasOlder: false, hasNewer: false });
	const [loading, setLoading] = useState({ initial: false, older: false, newer: false });
	const [failedIds, setFailedIds] = useState({});
	const [zoom, setZoom] = useState(null);

	const viewportRef = useRef(null);
	const dragRef = useRef(null);
	const touchStartRef = useRef(null);
	const abortRef = useRef({ initial: null, older: null, newer: null });
	// Passo pedido na ponta enquanto a janela seguinte ainda não chegou.
	const pendingStepRef = useRef(0);

	const sourceKey = source
		? `${source.kind}:${source.ticketId || ""}:${source.contactId || ""}:${JSON.stringify(source.filter || null)}`
		: "";
	const initialId = initialItem?.id || "";

	const abortAll = useCallback(() => {
		Object.values(abortRef.current).forEach((controller) => controller?.abort());
		abortRef.current = { initial: null, older: null, newer: null };
	}, []);

	useEffect(() => {
		if (!open || !initialItem) return undefined;

		abortAll();
		pendingStepRef.current = 0;
		setFailedIds({});
		setZoom(null);

		if (Array.isArray(localItems)) {
			const index = localItems.findIndex((item) => item.id === initialItem.id);
			setGallery(index >= 0 ? { items: localItems, index } : { items: [initialItem], index: 0 });
			setEdges({ hasOlder: false, hasNewer: false });
			setLoading({ initial: false, older: false, newer: false });
			return undefined;
		}

		setGallery({ items: [initialItem], index: 0 });
		setEdges({ hasOlder: false, hasNewer: false });

		if (!source) {
			setLoading({ initial: false, older: false, newer: false });
			return undefined;
		}

		const controller = new AbortController();
		abortRef.current.initial = controller;
		setLoading({ initial: true, older: false, newer: false });

		fetchMediaGallery(source, { cursorId: initialItem.id, direction: "around" }, controller.signal)
			.then(({ items, hasOlder, hasNewer }) => {
				if (controller.signal.aborted) return;
				const index = items.findIndex((item) => item.id === initialItem.id);
				const pending = pendingStepRef.current;
				pendingStepRef.current = 0;
				if (index < 0) return;
				// Seta apertada enquanto a primeira janela carregava: anda agora.
				const nextIndex = Math.min(Math.max(index + pending, 0), items.length - 1);
				setGallery({ items, index: nextIndex });
				setEdges({ hasOlder, hasNewer });
			})
			.catch((err) => {
				// Fora do escopo (ERR_MESSAGE_NOT_IN_SCOPE) ou falha de rede: fica só
				// o item clicado, que o usuário já estava vendo.
				if (!isRequestCanceled(err)) {
					console.warn("[MediaGalleryViewer] galeria indisponível:", err?.response?.data?.error || err?.message);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setLoading((prev) => ({ ...prev, initial: false }));
				}
			});

		return () => controller.abort();
		// Reinicia só ao abrir ou trocar de âncora/fonte — não a cada render do pai.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initialId, sourceKey]);

	// Lista local mudou com o visualizador aberto (mensagem nova, apagada): segue
	// na mídia que está na tela em vez de voltar para a que foi clicada.
	useEffect(() => {
		if (!open || !Array.isArray(localItems)) return;
		setGallery((prev) => {
			const currentId = prev.items[prev.index]?.id || initialId;
			const nextIndex = localItems.findIndex((item) => item.id === currentId);
			return nextIndex >= 0 ? { items: localItems, index: nextIndex } : prev;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localItems]);

	useEffect(() => {
		if (!open) abortAll();
		return abortAll;
	}, [open, abortAll]);

	const loadEdge = useCallback((side) => {
		if (!source || abortRef.current[side]) return;
		const cursor = side === "older" ? gallery.items[0] : gallery.items[gallery.items.length - 1];
		if (!cursor) return;

		const controller = new AbortController();
		abortRef.current[side] = controller;
		setLoading((prev) => ({ ...prev, [side]: true }));

		fetchMediaGallery(source, { cursorId: cursor.id, direction: side }, controller.signal)
			.then(({ items, hasOlder, hasNewer }) => {
				if (controller.signal.aborted) return;
				const pending = pendingStepRef.current;
				pendingStepRef.current = 0;
				setGallery((prev) => {
					if (side === "older") {
						const merged = mergeUnique(items, prev.items);
						const added = merged.length - prev.items.length;
						const step = pending < 0 && added > 0 ? -1 : 0;
						return { items: merged, index: prev.index + added + step };
					}
					const merged = mergeUnique(prev.items, items);
					const added = merged.length - prev.items.length;
					const step = pending > 0 && added > 0 ? 1 : 0;
					return { items: merged, index: prev.index + step };
				});
				setEdges((prev) => (side === "older" ? { ...prev, hasOlder } : { ...prev, hasNewer }));
			})
			.catch((err) => {
				if (isRequestCanceled(err)) return;
				console.warn("[MediaGalleryViewer] falha ao carregar mais mídias:", err?.message);
				pendingStepRef.current = 0;
				setEdges((prev) => (side === "older" ? { ...prev, hasOlder: false } : { ...prev, hasNewer: false }));
			})
			.finally(() => {
				if (abortRef.current[side] === controller) abortRef.current[side] = null;
				if (!controller.signal.aborted) {
					setLoading((prev) => ({ ...prev, [side]: false }));
				}
			});
	}, [source, gallery.items]);

	const { items, index } = gallery;
	const current = items[index] || null;

	// Busca a janela seguinte antes de o usuário chegar na ponta.
	useEffect(() => {
		if (!open || loading.initial) return;
		if (edges.hasOlder && index < PREFETCH_DISTANCE) loadEdge("older");
		if (edges.hasNewer && items.length - 1 - index < PREFETCH_DISTANCE) loadEdge("newer");
	}, [open, index, items.length, edges.hasOlder, edges.hasNewer, loading.initial, loadEdge]);

	// Pré-carrega só as fotos vizinhas: a API é HTTP/1.1 (6 conexões por host).
	useEffect(() => {
		if (!open) return;
		[items[index - 1], items[index + 1]].forEach((item) => {
			if (item?.type === "image" && item.url && !failedIds[item.id]) {
				const img = new Image();
				img.src = item.url;
			}
		});
	}, [open, index, items, failedIds]);

	useEffect(() => {
		setZoom(null);
	}, [current?.id]);

	const canGoPrev = index > 0 || edges.hasOlder || loading.initial;
	const canGoNext = index < items.length - 1 || edges.hasNewer || loading.initial;

	const goTo = useCallback((step) => {
		setGallery((prev) => {
			const nextIndex = prev.index + step;
			if (nextIndex < 0 || nextIndex >= prev.items.length) {
				// Na ponta com mais itens a caminho: anda assim que a janela chegar.
				pendingStepRef.current = step;
				return prev;
			}
			pendingStepRef.current = 0;
			return { ...prev, index: nextIndex };
		});
	}, []);

	const handleKeyDownCapture = (event) => {
		if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
		// Sem isto a seta também avança/retrocede o vídeo focado.
		event.preventDefault();
		event.stopPropagation();
		goTo(event.key === "ArrowLeft" ? -1 : 1);
	};

	const handleImageClick = (event) => {
		if (dragRef.current?.moved) return;
		if (zoom) {
			setZoom(null);
			return;
		}
		const img = event.currentTarget;
		const rect = img.getBoundingClientRect();
		// Tamanho natural; foto pequena ainda dobra, senão o clique não faria nada.
		const width = Math.max(img.naturalWidth || 0, rect.width * 2);
		setZoom({
			width,
			ratioX: rect.width ? (event.clientX - rect.left) / rect.width : 0.5,
			ratioY: rect.height ? (event.clientY - rect.top) / rect.height : 0.5,
		});
	};

	// Leva o ponto clicado para o centro da tela depois de ampliar.
	useLayoutEffect(() => {
		const viewport = viewportRef.current;
		if (!zoom || !viewport) return;
		const img = viewport.querySelector("img");
		if (!img) return;
		viewport.scrollLeft = zoom.ratioX * img.offsetWidth - viewport.clientWidth / 2;
		viewport.scrollTop = zoom.ratioY * img.offsetHeight - viewport.clientHeight / 2;
	}, [zoom]);

	const handleMouseDown = (event) => {
		if (!zoom || !viewportRef.current) return;
		event.preventDefault();
		dragRef.current = {
			x: event.clientX,
			y: event.clientY,
			left: viewportRef.current.scrollLeft,
			top: viewportRef.current.scrollTop,
			moved: false,
		};
	};

	const handleMouseMove = (event) => {
		const drag = dragRef.current;
		if (!drag || !viewportRef.current) return;
		const dx = event.clientX - drag.x;
		const dy = event.clientY - drag.y;
		if (Math.abs(dx) > DRAG_CLICK_TOLERANCE_PX || Math.abs(dy) > DRAG_CLICK_TOLERANCE_PX) {
			drag.moved = true;
		}
		viewportRef.current.scrollLeft = drag.left - dx;
		viewportRef.current.scrollTop = drag.top - dy;
	};

	const handleMouseUp = () => {
		// O click dispara depois do mouseup: limpa só no próximo ciclo.
		setTimeout(() => {
			dragRef.current = null;
		}, 0);
	};

	const handleTouchStart = (event) => {
		if (zoom || event.touches.length !== 1) return;
		touchStartRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
	};

	const handleTouchEnd = (event) => {
		const start = touchStartRef.current;
		touchStartRef.current = null;
		if (!start || zoom) return;
		const touch = event.changedTouches[0];
		const dx = touch.clientX - start.x;
		const dy = touch.clientY - start.y;
		if (Math.abs(dx) >= SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy)) {
			goTo(dx > 0 ? -1 : 1);
		}
	};

	const markFailed = (id) => setFailedIds((prev) => ({ ...prev, [id]: true }));

	const handleGoToMessage = () => {
		if (!current) return;
		onClose();
		onGoToMessage(current);
	};

	const renderMedia = () => {
		if (!current) return null;

		if (!current.url || failedIds[current.id]) {
			return (
				<div className={classes.unavailable}>
					<BrokenImageIcon className={classes.unavailableIcon} />
					<span>{i18n.t("messagesList.media.unavailable")}</span>
				</div>
			);
		}

		if (current.type === "video") {
			return (
				<video
					key={current.id}
					className={classes.video}
					src={current.url}
					poster={current.thumbUrl}
					controls
					autoPlay
					onError={() => markFailed(current.id)}
				/>
			);
		}

		return (
			<img
				key={current.id}
				src={current.url}
				alt={current.fileName}
				title={i18n.t(zoom ? "messagesList.mediaViewer.zoomOut" : "messagesList.mediaViewer.zoomIn")}
				draggable={false}
				className={`${classes.image} ${zoom ? classes.imageZoomed : ""}`}
				style={zoom ? { width: zoom.width } : undefined}
				onClick={handleImageClick}
				onError={() => markFailed(current.id)}
			/>
		);
	};

	const renderNavIcon = (side) => {
		const isLoading = loading.initial || (side === "prev" ? loading.older && index === 0 : loading.newer && index === items.length - 1);
		if (isLoading) return <CircularProgress size={24} style={{ color: "#fff" }} />;
		return side === "prev" ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />;
	};

	if (!open || !initialItem) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullScreen
			classes={{ paper: classes.paper }}
			onKeyDownCapture={handleKeyDownCapture}
		>
			<div className={classes.header}>
				<div className={classes.headerInfo}>{formatItemDate(current?.createdAt)}</div>
				{onGoToMessage && current && (
					<Button
						size="small"
						className={classes.goToButton}
						startIcon={<ChatBubbleOutline fontSize="small" />}
						onClick={handleGoToMessage}
					>
						{i18n.t("messagesList.mediaViewer.goToMessage")}
					</Button>
				)}
				{current?.url && (
					<Tooltip title={i18n.t("messagesList.media.download")}>
						<IconButton
							className={classes.headerButton}
							onClick={() => downloadMediaFile(current.url, current.fileName)}
							aria-label={i18n.t("messagesList.media.download")}
						>
							<GetApp />
						</IconButton>
					</Tooltip>
				)}
				<Tooltip title={i18n.t("common.close")}>
					<IconButton className={classes.headerButton} onClick={onClose} aria-label={i18n.t("common.close")}>
						<CloseIcon />
					</IconButton>
				</Tooltip>
			</div>

			<div className={classes.stage}>
				<Tooltip title={i18n.t("common.previous")}>
					<span className={`${classes.navSlot} ${classes.navPrev}`}>
						<IconButton
							className={classes.navButton}
							disabled={!canGoPrev}
							onClick={() => goTo(-1)}
							aria-label={i18n.t("common.previous")}
						>
							{renderNavIcon("prev")}
						</IconButton>
					</span>
				</Tooltip>

				<div
					ref={viewportRef}
					className={`${classes.viewport} ${zoom ? classes.viewportZoomed : ""}`}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={handleMouseUp}
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
				>
					{renderMedia()}
				</div>

				<Tooltip title={i18n.t("common.next")}>
					<span className={`${classes.navSlot} ${classes.navNext}`}>
						<IconButton
							className={classes.navButton}
							disabled={!canGoNext}
							onClick={() => goTo(1)}
							aria-label={i18n.t("common.next")}
						>
							{renderNavIcon("next")}
						</IconButton>
					</span>
				</Tooltip>
			</div>
		</Dialog>
	);
};

export default MediaGalleryViewer;
